"use client";

import { Icon } from "@ospweb/sdk-ui";
import { useCallback, useRef, useState } from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/Paywall/GoalActivationPaywallCarousel";

const I18n = I18nUtil.createScoped(Translations);

export const GOAL_ACTIVATION_PAYWALL_SLIDES = [
  {
    headline: "Plans every step and keeps it moving with you",
    visualLabel: "Plan steps",
    icon: "checklist" as const,
    visual: "steps",
  },
  {
    headline: "Always watching everything tied to this goal",
    visualLabel: "Goal network",
    icon: "target" as const,
    visual: "network",
  },
  {
    headline: "Tells you the moment there’s news or something to do",
    visualLabel: "Notifications",
    icon: "notification" as const,
    visual: "notifications",
  },
  {
    headline: "You change, Ed adjusts the plan",
    visualLabel: "Adaptive plan",
    icon: "refresh" as const,
    visual: "adaptive",
  },
] as const;

function GoalPaywallVisual({
  visual,
  icon,
  isPc = false,
}: Pick<(typeof GOAL_ACTIVATION_PAYWALL_SLIDES)[number], "visual" | "icon"> & {
  isPc?: boolean;
}) {
  if (visual === "steps") {
    return (
      <div
        className="flex w-full flex-col gap-2 px-2"
        data-testid="goal-paywall-visual-steps"
      >
        {[
          [
            "Put your idle cash to work",
            "≈$40k sitting idle → a 4.5% position.",
          ],
          [
            "Lock a sharper mortgage rate",
            "Your refinance window is open right now.",
          ],
          [
            "Rebalance to reach $1M sooner",
            "Shift your mix so the milestone arrives years earlier.",
          ],
        ].map(([label, summary], step) => (
          <span
            key={label}
            className={cn(
              "flex items-start gap-3 rounded-2 px-3 py-3 text-left",
              step === 0
                ? "bg-background_brand_overlay100 text-content_inverse_primary"
                : "bg-background_inverse_overlay40 text-content_inverse_primary",
            )}
          >
            <b
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full body_medium_m",
                step === 0
                  ? "bg-content_aura_lv3_primary text-content_primary"
                  : "bg-background_brand_overlay100 text-content_inverse_primary",
              )}
            >
              {step + 1}
            </b>
            <span className="flex min-w-0 flex-col gap-1">
              <strong className="body_bold_m">{I18n.t(label)}</strong>
              <em className="not-italic body_s opacity-80">
                {I18n.t(summary)}
              </em>
            </span>
          </span>
        ))}
      </div>
    );
  }
  if (visual === "network") {
    return (
      <div
        className="relative h-72 w-full max-w-[340px] body_s text-content_inverse_primary"
        data-testid="goal-paywall-visual-network"
      >
        <span className="absolute left-1/2 top-1/2 size-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dotted border-white/55" />
        <span className="absolute left-1/2 top-1/2 size-[210px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dotted border-white/45" />
        <span className="absolute left-1/2 top-1/2 flex h-[88px] w-[260px] -translate-x-1/2 -translate-y-1/2 flex-col items-start justify-center gap-1 rounded-2 bg-background_brand_overlay100 px-5 text-left">
          <span className="flex items-center gap-2 text-[12px] text-content_aura_lv3_primary">
            <Icon name={icon} size={15} /> {I18n.t("Your goal")}
          </span>
          <strong className="text-[16px] font-semibold text-white">
            {I18n.t("Build a 6-month safety net")}
          </strong>
        </span>
        {[
          ["left-[15px] top-[48px]", "Your income came late"],
          ["right-[31px] top-[26px]", "Savings rates moved"],
          ["right-[41px] top-[201px]", "A bill went up"],
        ].map(([position, label]) => (
          <i
            key={label}
            className={cn(
              "absolute rounded-2 bg-white px-3 py-2 not-italic text-content_primary shadow-sm",
              position,
            )}
          >
            <span className="mr-1 text-content_brand_primary">•</span>
            {I18n.t(label)}
          </i>
        ))}
      </div>
    );
  }
  if (visual === "notifications") {
    return (
      <div
        className={cn(
          "relative w-full max-w-[416px] overflow-hidden",
          isPc ? "h-[300px]" : "h-[316px]",
        )}
        data-testid="goal-paywall-visual-notifications"
      >
        <div
          className={cn(
            "absolute left-1/2 h-[316px] w-[361px] -translate-x-1/2",
            isPc ? "-top-3" : "top-0",
          )}
          style={{
            maskImage:
              "linear-gradient(to bottom, black 0px, black 273px, transparent 316px)",
          }}
        >
          <div className="absolute left-[31px] top-4 h-[372px] w-[300px] overflow-hidden rounded-[34px] border-[6px] border-content_primary bg-content_tertiary">
            <span className="absolute left-[96px] top-[6px] h-5 w-24 rounded-[10px] bg-background_black_overlay40" />
            {[
              {
                title: "A better rate just opened up",
                summary: "It beats the one you're on. I'll show you where.",
                top: "top-[124px]",
              },
              {
                title: "Heads up — a bill went up",
                summary:
                  "It eats into this month's set-aside. Want me to adjust?",
                top: "top-[208px]",
              },
            ].map(({ title, summary, top }) => (
              <div
                key={title}
                data-testid="goal-paywall-notification"
                className={cn(
                  "absolute left-[10px] flex h-[70px] w-[268px] items-start gap-[10px] overflow-hidden rounded-[16px] bg-background_inverse_overlay20 p-[10px] text-left text-content_inverse_primary",
                  top,
                )}
              >
                <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[6px] bg-content_brand_primary text-content_accent7_primary">
                  <Icon name="ed-logo" size={19} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex items-center gap-2">
                    <strong className="min-w-0 flex-1 truncate body_bold_s">
                      {I18n.t(title)}
                    </strong>
                    <span className="shrink-0 body_medium_xs text-content_inverse_secondary">
                      {I18n.t("now")}
                    </span>
                  </span>
                  <span className="body_s text-content_inverse_secondary">
                    {I18n.t(summary)}
                  </span>
                </span>
              </div>
            ))}
          </div>
          <div
            data-testid="goal-paywall-notification"
            className="absolute left-[19px] top-[66px] flex h-[62px] w-[324px] items-start gap-[10px] overflow-hidden rounded-[16px] bg-background_solid_neutral_primary p-[10px] text-left text-content_primary"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-[7px] bg-content_brand_primary text-content_accent7_primary">
              <Icon name="ed-logo" size={22} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center justify-between gap-2">
                <strong className="min-w-0 truncate body_bold_m">
                  {I18n.t("NVDA ranking watch moved")}
                </strong>
                <span className="shrink-0 body_medium_s text-content_secondary">
                  {I18n.t("now")}
                </span>
              </span>
              <span className="truncate body_m text-content_secondary">
                {I18n.t("It slipped out of the top 3 on profitability.")}
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="goal-paywall-visual-adaptive"
      className={cn(
        "relative w-[366px]",
        // 卡片实际占 231px；移动端用 150px 容器 + 上移 80px 做层叠溢出效果，
        // PC 端上方 header (z-20) 会盖住上移的卡片 1，改为真实高度、不上移。
        isPc ? "h-[231px]" : "h-[150px] -translate-y-20",
      )}
    >
      {[
        "Put your idle cash to work",
        "Lock a sharper mortgage rate",
        "Rebalance to reach $1M sooner",
        "Top up your emergency buffer",
      ].map((copy, index) => (
        <i
          key={copy}
          className={cn(
            "absolute flex h-[52px] items-center gap-3 rounded-[14px] px-3 py-3 not-italic text-left",
            index === 1
              ? "left-[15px] top-[60px] w-[340px] bg-background_inverse_overlay100 text-content_primary"
              : index === 2
                ? "left-[40px] top-[119px] w-[340px] bg-content_primary text-content_inverse_primary"
                : index === 3
                  ? "left-[47px] top-[179px] w-[300px] bg-background_inverse_overlay40 text-content_inverse_primary"
                  : "left-[47px] top-0 w-[300px] bg-background_inverse_overlay40 text-content_inverse_primary",
          )}
        >
          <b
            className={cn(
              "flex size-7 shrink-0 items-center justify-center rounded-full body_medium_s",
              index === 2
                ? "bg-content_aura_lv3_primary text-content_primary"
                : "bg-content_brand_primary text-content_inverse_primary",
            )}
          >
            {index + 1}
          </b>
          <span
            className={cn(
              index === 1 || index === 2 ? "title_m" : "body_bold_s",
            )}
          >
            {I18n.t(copy)}
          </span>
        </i>
      ))}
      <GoalPaywallAdaptiveArrow className="left-[11px] top-[119px]" />
      <GoalPaywallAdaptiveArrow flipped className="right-[11px] top-[105px]" />
    </div>
  );
}

/**
 * 屏 4 侧边弧形箭头 —— Figma `107353:217792/217795`（Group 2147235400/5401，
 * 从 366×247 卡组 mask 摘出的同一矢量；右侧为同形 rotate-180）。
 * 此前用文本字符 ↰/↱ 复刻，字形与设计稿完全不符（PC 上即乱，非小屏专属）。
 */
function GoalPaywallAdaptiveArrow({
  flipped = false,
  className,
}: {
  flipped?: boolean;
  className?: string;
}) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 18.0832 30.802"
      fill="none"
      className={cn(
        "absolute h-[31px] w-[18px] text-content_inverse_primary",
        className,
      )}
      style={flipped ? { transform: "rotate(180deg)" } : undefined}
    >
      <path
        d="M6.45817 1.29167L6.45817 22.4557C6.45817 24.3267 7.18396 26.1211 8.47588 27.4441C9.7678 28.7671 11.52 29.5103 13.3471 29.5103L16.7915 29.5103"
        stroke="currentColor"
        strokeWidth="2.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.625 6.58289L6.45833 1.29189L1.29167 6.58289"
        stroke="currentColor"
        strokeWidth="2.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** F07–F10 Goal-only hero. Product and price stay in the shared billing footer. */
export function GoalActivationPaywallCarousel({
  isPc = false,
}: {
  isPc?: boolean;
}) {
  const [index, setIndex] = useState(() => {
    if (typeof window === "undefined") {
      return 0;
    }
    const requested = Number(
      new URLSearchParams(window.location.search).get("goalPaywallSlide"),
    );
    return Number.isInteger(requested) && requested >= 1 && requested <= 4
      ? requested - 1
      : 0;
  });
  const [direction, setDirection] = useState<-1 | 1>(1);
  const swipeStart = useRef<{
    x: number;
    y: number;
    startedAt: number;
  } | null>(null);
  const slide = GOAL_ACTIVATION_PAYWALL_SLIDES[index]!;

  const move = useCallback((direction: -1 | 1) => {
    setDirection(direction);
    setIndex((current) =>
      Math.min(
        GOAL_ACTIVATION_PAYWALL_SLIDES.length - 1,
        Math.max(0, current + direction),
      ),
    );
  }, []);

  return (
    <section
      data-testid="goal-activation-paywall-carousel"
      data-or-view="goal_activate_paywall_view"
      data-or-param-source="goal_activate"
      data-or-param-slide={index + 1}
      data-goal-paywall-slide={index + 1}
      data-swipe-threshold="0.3"
      data-navigation-mode={isPc ? "arrows" : "swipe"}
      data-figma-node={
        isPc
          ? ["108870:20667", "108870:20728", "108870:20796", "108870:20883"][
              index
            ]
          : ["108870:20078", "108870:20142", "108870:20210", "108870:20298"][
              index
            ]
      }
      className={cn(
        "flex min-h-0 flex-1 touch-pan-y flex-col",
        isPc && slide.visual === "notifications"
          ? "justify-start gap-2 pb-0 pt-4"
          : slide.visual === "notifications"
            ? "justify-start gap-4 pb-0 pt-2"
            : "justify-center gap-6 py-5",
        !isPc &&
          (slide.visual === "steps"
            ? "translate-y-20"
            : slide.visual === "adaptive"
              ? "translate-y-[140px]"
              : slide.visual === "notifications"
                ? ""
                : "translate-y-[30px]"),
      )}
      onPointerDown={(event) => {
        if (isPc || event.isPrimary === false) {
          return;
        }
        swipeStart.current = {
          x: event.clientX,
          y: event.clientY,
          startedAt: Date.now(),
        };
      }}
      onPointerUp={(event) => {
        const from = swipeStart.current;
        swipeStart.current = null;
        if (from === null) {
          return;
        }
        const distanceX = event.clientX - from.x;
        const distanceY = event.clientY - from.y;
        const distanceThreshold =
          event.currentTarget.getBoundingClientRect().width * 0.3;
        const isFastSwipe =
          Date.now() - from.startedAt <= 300 && Math.abs(distanceX) >= 48;
        const isHorizontalGesture =
          Math.abs(distanceX) > Math.abs(distanceY) * 1.2;
        if (
          isHorizontalGesture &&
          (Math.abs(distanceX) >= distanceThreshold || isFastSwipe)
        ) {
          move(distanceX < 0 ? 1 : -1);
        }
      }}
      onPointerCancel={() => {
        swipeStart.current = null;
      }}
    >
      <style>{`
        @keyframes goal-paywall-slide-forward { from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes goal-paywall-slide-back { from { opacity: 0; transform: translateX(-28px); } to { opacity: 1; transform: translateX(0); } }
        @media (max-height: 820px) and (max-width: 767px) {
          [data-goal-paywall-slide="3"] .goal-paywall-headline { margin-top: 12px; }
        }
      `}</style>
      <div
        className={cn(
          "relative",
          slide.visual === "notifications"
            ? isPc
              ? "px-4 py-0"
              : "px-0 py-0"
            : isPc
              ? "px-12 py-2"
              : "px-2 py-2",
          slide.visual === "adaptive" ? "overflow-visible" : "overflow-hidden",
        )}
      >
        <div
          key={slide.visual}
          className="flex min-h-44 flex-col items-center justify-center gap-4 text-center"
          data-testid="goal-activation-paywall-slide"
          style={{
            animation: `goal-paywall-slide-${direction > 0 ? "forward" : "back"} ${direction > 0 ? 350 : 300}ms cubic-bezier(0.32, 0.72, 0, 1) both`,
          }}
        >
          <GoalPaywallVisual
            visual={slide.visual}
            icon={slide.icon}
            isPc={isPc}
          />
        </div>
        {isPc && index > 0 ? (
          <button
            type="button"
            aria-label={I18n.t("Previous goal paywall slide")}
            data-testid="goal-paywall-previous"
            data-openreplay-label="goal_activation_paywall_previous"
            className="absolute left-1 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-background_black_overlay40 text-content_inverse_primary backdrop-blur-[10px] transition-colors hover:bg-background_inverse_overlay20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            onClick={() => move(-1)}
          >
            <Icon name="left-arrow-alt" size={20} />
          </button>
        ) : null}
        {isPc && index < GOAL_ACTIVATION_PAYWALL_SLIDES.length - 1 ? (
          <button
            type="button"
            aria-label={I18n.t("Next goal paywall slide")}
            data-testid="goal-paywall-next"
            data-openreplay-label="goal_activation_paywall_next"
            className="absolute right-1 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-background_black_overlay40 text-content_inverse_primary backdrop-blur-[10px] transition-colors hover:bg-background_inverse_overlay20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            onClick={() => move(1)}
          >
            <Icon name="right-arrow-alt" size={20} />
          </button>
        ) : null}
      </div>
      <h1
        className={cn(
          "goal-paywall-headline text-center headline_regular_m text-content_inverse_primary",
          isPc && slide.visual === "notifications" && "w-[361px] self-center",
          !isPc && slide.visual === "notifications" && "mt-6",
        )}
        data-testid="goal-paywall-headline"
        aria-live="polite"
      >
        {I18n.t(slide.headline)}
      </h1>
      <div
        className={cn(
          "flex items-center justify-center gap-2",
          isPc && slide.visual === "notifications" && "mt-1",
        )}
        aria-label={I18n.t("Goal paywall progress")}
      >
        {GOAL_ACTIVATION_PAYWALL_SLIDES.map((item, itemIndex) => (
          <span
            key={item.headline}
            data-testid="goal-paywall-indicator"
            data-active={String(itemIndex === index)}
            aria-hidden
            className={cn(
              "size-1.5 rounded-full transition-all",
              itemIndex === index
                ? "bg-background_inverse_overlay100"
                : "bg-background_inverse_overlay40",
            )}
          />
        ))}
      </div>
    </section>
  );
}
